import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { CostCodeTag } from '@client/components/claims/financials/CostCodeTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { buildCurrencyInputRules, cn } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Checkbox, Form, Tooltip } from 'antd';
import Decimal from 'decimal.js';
import { CircleQuestionMark } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { claimFormStore } from '../../../store';
import { useCompensationStore } from './store';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/add/excess-payments/add',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Link excess',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessPaymentAdd />
        </PageController>
    );
}

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['excess']['listExcesses']>['rows'][number],
    NonNullable<RouterInputs['excess']['listExcesses']['orderBy']>
>();

type FormData = Record<
    string /* excessId */,
    {
        amountToAllocate: Decimal;
        isInsurerApproved: boolean;
    }
>;

const FormItem = Form.Item<FormData>;

function ExcessPaymentAdd() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const compensation = useCompensationStore((state) => state.compensation);
    const compensationId = useCompensationStore((state) => state.compensationId);
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate, isPending } = trpc.excessPayment.createManyExcessPaymentsForCompensation.useMutation();
    const { formatCurrency } = useFormatter();
    const navigate = useNavigate();

    const [input, setInput] = useState<RouterInputs['excess']['listExcesses']>({
        orderBy: { id: 'desc' },
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([]);
    const [error, setError] = useState<ReactNode>(undefined);

    const { data, isPending: isPendingListExcesses } = trpc.excess.listExcesses.useQuery({
        ...input,
        where: {
            ...input.where,
            claimId: id,
            compensationId,
            status: {
                in: ['UNRESOLVED', 'PENDING', 'PARTIALLY_SETTLED'],
            },
        },
    });

    const [form] = Form.useForm<FormData>();
    const values = Form.useWatch([], form);

    const canModify = !isReadonly && compensation;

    useEffect(() => {
        if (!canModify) {
            navigate({ moveInMatches: -1 });
        }
    }, [canModify]);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPendingListExcesses,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'none',
        },
        rowSelection: {
            enabled: true,
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRowKeys.map(BigInt),
            onChange(selectedKeys) {
                setSelectedRowKeys(selectedKeys.map(BigInt));

                const values = form.getFieldsValue();

                form.setFieldsValue(
                    Object.fromEntries(
                        Object.keys(values).map((id) => {
                            const modifiedId = BigInt(id);
                            const isSelected = selectedKeys.includes(modifiedId);

                            return [
                                id,
                                {
                                    amountToAllocate: isSelected
                                        ? data?.rows.find((item) => item.id === modifiedId)?.remainingAmount ||
                                          undefined
                                        : undefined,
                                    isInsurerApproved: isSelected && !!values[id]?.isInsurerApproved,
                                },
                            ];
                        }),
                    ),
                );
            },
            getCheckboxProps(row) {
                return {
                    disabled: row.remainingAmount.lte(0),
                };
            },
        },
        columns: [
            columnHelper.accessor((row) => row.TransactionType?.name, {
                id: 'transactionType',
                header: 'Transaction type',
                sortable: 'TransactionType.name',
                cell({ row }) {
                    return row.TransactionType?.name;
                },
            }),
            columnHelper.accessor('costCodeFullName', {
                id: 'costCodeFullName',
                header: 'Cost code',
                cell({ row }) {
                    return (
                        <CostCodeTag costCode={row.costCodeFullName} isDisabled={!selectedRowKeys.includes(row.id)} />
                    );
                },
            }),
            columnHelper.accessor('remainingAmount', {
                id: 'remainingAmount',
                header: 'Amount available',
                cell({ row }) {
                    return formatCurrency(row.remainingAmount);
                },
            }),
            columnHelper.display({
                id: 'amountToAllocate',
                header: 'Amount to allocate',
                className: 'max-w-3xs',
                cell({ row }) {
                    return (
                        <FormItem
                            name={[String(row.id), 'amountToAllocate']}
                            className="mb-0"
                            rules={buildCurrencyInputRules({
                                isRequired: selectedRowKeys.includes(row.id),
                                gtZero: true,
                                max: row.remainingAmount,
                            })}
                        >
                            <CurrencyInput isDisabled={!selectedRowKeys.includes(row.id)} />
                        </FormItem>
                    );
                },
            }),
            columnHelper.display({
                id: 'remainingAmountAfter',
                header: 'Remaining amount',
                cell({ row }) {
                    const amountToAllocate = values?.[String(row.id)]?.amountToAllocate || new Decimal(0);

                    return formatCurrency(
                        amountToAllocate.gt(row.remainingAmount)
                            ? row.remainingAmount
                            : row.remainingAmount.minus(amountToAllocate),
                    );
                },
            }),
            columnHelper.display({
                id: 'insurerApproved',
                header: (
                    <div className="flex items-center gap-1">
                        Insurer approved
                        <Tooltip title="Did the insurer instruct the excess to be paid to the supplier?">
                            <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                        </Tooltip>
                    </div>
                ),
                cell({ row }) {
                    return (
                        <FormItem name={[String(row.id), 'isInsurerApproved']} className="mb-0" valuePropName="checked">
                            <Checkbox disabled={!selectedRowKeys.includes(row.id)} />
                        </FormItem>
                    );
                },
            }),
        ],
    });

    if (!canModify) {
        return null;
    }

    return (
        <OverlayPage
            afterClose={() => navigate({ moveInMatches: -1 })}
            title="Link excess"
            size="xLarge"
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        loading={isPending}
                        disabled={!selectedRowKeys.length}
                    >
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    id={formId}
                    disabled={isPending}
                    onFinish={(values) => {
                        if (!compensationId) {
                            return;
                        }

                        message.loading('Saving...');

                        const excessPayments = Object.entries(values)
                            .filter(([excessId]) => selectedRowKeys.includes(BigInt(excessId)))
                            .map(([excessId, { amountToAllocate, isInsurerApproved }]) => ({
                                excessId: BigInt(excessId),
                                amountToAllocate,
                                isInsurerApproved,
                            }));
                        const totalAmountToAllocate = excessPayments.reduce(
                            (prev, { amountToAllocate }) => prev.plus(amountToAllocate),
                            new Decimal(0),
                        );

                        mutate(
                            {
                                compensationId,
                                excessPayments,
                            },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    void trpcUtils.excessPayment.listExcessPayments.invalidate();
                                    void trpcUtils.compensation.getCompensationSummary.invalidate();
                                    void trpcUtils.compensationPayment.listCompensationPayments.invalidate();
                                    onClose();
                                },
                                onError(error) {
                                    if (
                                        error.data?.errorId ===
                                        'excessPayment.totalExcessAmountGreaterThanPaymentItemsTotalAmount'
                                    ) {
                                        message.destroy();

                                        setError(
                                            <>
                                                The total excess allocation ({formatCurrency(totalAmountToAllocate)})
                                                exceeds the payment items total amount.
                                                <br />
                                                Please reduce the excess allocation amount or add more payment items.
                                            </>,
                                        );

                                        return;
                                    }

                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    {error && <p className="mb-2 text-(--ant-color-error)">{error}</p>}
                    <PrismaTable
                        {...tableProps}
                        onRow={({ remainingAmount }) => ({
                            className: cn(remainingAmount.lte(0) && 'text-(--ant-color-text-disabled)'),
                        })}
                    />
                </Form>
            )}
        </OverlayPage>
    );
}
